// ---------------------------------------------------------------------------
// StatusFlowBar — compact horizontal stepper (props in → UI out).
//
// A condensed lifecycle indicator: one pill per step, the current step
// highlighted, earlier steps marked done, later steps dimmed. Optionally the
// whole bar is a button that opens a fuller flow view (e.g. the Saksflyt tab).
//
// Styling is delegated entirely to the .statusbar / .statusstep /
// .statusbar__arrow family in shared/styles/components.css (token-backed,
// ported from the prototype's CaseStatusBar). No data fetching, no auth logic,
// no surface assumptions — safe for the public sessionless register bundle.
//
// Tone drives the active-step color (.statusstep--<tone>); done/active/todo
// is derived from the step's position relative to `current`.
// ---------------------------------------------------------------------------

import { Fragment } from 'react'

export type StatusFlowTone = 'info' | 'warn' | 'accent' | 'success' | 'danger'

export interface StatusFlowStep {
  /** Stable identifier for the step (matches the `current` value of the active step). */
  id: string
  /** Visible label (already i18n'd by the caller). */
  label: string
  /** Active-step color tone. Omit for the neutral default. */
  tone?: StatusFlowTone
}

export interface StatusFlowBarProps {
  steps: StatusFlowStep[]
  /** id of the currently active step. */
  current: string
  /**
   * Optional click handler. When provided the bar becomes an interactive
   * button (e.g. to open the full Saksflyt view); otherwise it is a static
   * presentational strip.
   */
  onOpen?: () => void
  /** Accessible label for the interactive bar (already i18n'd). */
  ariaLabel?: string
}

/**
 * Compact lifecycle stepper.
 *
 * Usage:
 *   <StatusFlowBar
 *     steps={[{ id: 'open', label: t('…'), tone: 'info' }, …]}
 *     current="under_review"
 *     onOpen={() => setTab('saksflyt')}
 *     ariaLabel={t('admin.cases.show.open_flow')}
 *   />
 */
export function StatusFlowBar({ steps, current, onOpen, ariaLabel }: StatusFlowBarProps) {
  const currentIndex = steps.findIndex((s) => s.id === current)

  const content = steps.map((step, i) => {
    const isActive = step.id === current
    const isDone = currentIndex !== -1 && i < currentIndex
    const stateClass = isActive ? 'is-active' : isDone ? 'is-done' : 'is-todo'
    const toneClass = step.tone ? `statusstep--${step.tone}` : ''

    return (
      // The step id is stable, so it is a safe React key. The Fragment keeps
      // the arrow + step as direct children of .statusbar so the CSS flex gap
      // applies (mirrors the prototype's CaseStatusBar markup).
      <Fragment key={step.id}>
        {i > 0 && (
          <span
            className={`statusbar__arrow${i <= currentIndex ? ' is-done' : ''}`}
            aria-hidden="true"
          />
        )}
        <span className={`statusstep ${toneClass} ${stateClass}`.replace(/\s+/g, ' ').trim()} title={step.label}>
          <span className="statusstep__dot" aria-hidden="true" />
          <span className="statusstep__txt">{step.label}</span>
        </span>
      </Fragment>
    )
  })

  if (onOpen) {
    return (
      <button type="button" className="statusbar" onClick={onOpen} aria-label={ariaLabel}>
        {content}
      </button>
    )
  }

  return (
    <div className="statusbar" aria-label={ariaLabel} role="group">
      {content}
    </div>
  )
}
